import {compact} from '@benchling/js-utils';
import reactUpdate, {Spec} from 'immutability-helper';
import _ from 'underscore';

import {assert} from 'Asserts';
import * as biojs from 'BioJS';
import {SequenceTopology} from 'constants/SequenceConstants';
import * as colors from 'lib/Colors';
import deepCopy from 'lib/deepCopy';
import {WorklistInfo} from 'molbio/components/SaveLocationPicker';
import {listToReadable} from 'util/NameUtil';
import pick from 'util/pick';
import {WorklistFormType} from 'worklists/WorklistConstants';

import {
  BULK_ASSEMBLY_FINALIZE_IDX,
  BULK_ASSEMBLY_OUTPUT_TYPE_TO_DISPLAY_STRING,
  BULK_ASSEMBLY_STATE,
  BulkAssemblyNewWorklistInfo,
  BulkAssemblyOutputType,
  DataSourceType,
  Endpoint,
  EndpointType,
  EnzymeEndpoint,
  GibsonStatus,
  HomologyEndpoint,
  LocalAssembly,
  LocalDataSource,
  Slot,
  SlotSummary,
  Summary,
} from './BulkAssemblyConstants';

export interface SlotParams {
  endpoints: Array<Endpoint>;
  name: string;
  schemaFieldId: string | null;
  useLargerFragment: boolean | undefined;
  useReverse: boolean;
  fileIds: Array<string>;
}

function applyUpdateForId(objId: string, update: Spec<LocalDataSource>): Spec<Array<LocalDataSource>> {
  return {
    $apply: (objs: Array<LocalDataSource>) =>
      // TS gets confused by the reactUpdate types, so just use any for now.
      // oxlint-disable-next-line @typescript-eslint/no-explicit-any
      objs.map((obj) => (obj.id !== objId ? obj : reactUpdate(obj, update as any))),
  };
}

function sanitizeAssembly(assembly: LocalAssembly): LocalAssembly {
  const slotsByDataSource = _.groupBy(assembly.slots, 'dataSourceId');
  return reactUpdate(assembly, {
    dataSources: {
      $apply: (dataSources: Array<LocalDataSource>) =>
        // eslint-disable-next-line benchlint/no-in-operator
        dataSources.filter(({id}: {id: string}) => id in slotsByDataSource),
    },
    meta: {
      activeSlotIndex: {
        $set:
          assembly.meta.activeSlotIndex === BULK_ASSEMBLY_FINALIZE_IDX
            ? BULK_ASSEMBLY_FINALIZE_IDX
            : Math.min(assembly.meta.activeSlotIndex, assembly.slots.length - 1),
      },
    },
  });
}

function createDataSource(): LocalDataSource {
  return {
    id: _.uniqueId('bulkAssembly-ds'),
    fileIds: [],
  };
}

export function createSlot({
  dataSourceId,
  slotName,
  useLargerFragment,
}: {
  dataSourceId: string | undefined;
  useLargerFragment: boolean | undefined;
  slotName: string;
}): Slot {
  return {
    slotParams: {
      endpoints: [getDefaultEndpointArgs(EndpointType.ENZYME), getDefaultEndpointArgs(EndpointType.ENZYME)],
      useLargerFragment,
      useReverse: false,
      schemaFieldId: null,
      name: slotName,
    },
    dataSourceId,
    dataSourceType: DataSourceType.FOLDERS,
  };
}

function updateAssemblyState(
  assembly: LocalAssembly,
  state: string,
  extra: Summary | {} = {}
): LocalAssembly {
  return reactUpdate(assembly, {
    meta: {
      $merge: {
        state,
        ...extra,
      },
    },
  });
}

/**
 * Immutability helpers for assemblies
 */
export const Update = {
  setTopology(assembly: LocalAssembly, topology: SequenceTopology): LocalAssembly {
    const newAssembly = {...assembly, topology};
    return this.updateSlotNames(newAssembly);
  },

  resetSchemaFields(assembly: LocalAssembly): LocalAssembly {
    // eslint-disable-next-line no-restricted-syntax
    const newAssembly = _.reduce(
      assembly.slots,
      (memo: LocalAssembly, slot: Slot, slotIdx: number): LocalAssembly =>
        reactUpdate(memo, {
          slots: {[slotIdx]: {slotParams: {$merge: {schemaFieldId: null}}}},
        }),
      assembly
    );
    return sanitizeAssembly(newAssembly);
  },

  resetSlotDataSource(
    assembly: LocalAssembly,
    slotIdx: number,
    dataSourceType: DataSourceType
  ): LocalAssembly {
    const dataSource = createDataSource();
    const newAssembly = reactUpdate(assembly, {
      dataSources: {$push: [dataSource]},
      slots: {[slotIdx]: {$merge: {dataSourceId: dataSource.id, dataSourceType}}},
    });
    // Remove old data source if it is no longer referenced.
    return sanitizeAssembly(newAssembly);
  },

  setSlotDataSource(assembly: LocalAssembly, slotIdx: number, dataSourceId: string): LocalAssembly {
    const newAssembly = reactUpdate(assembly, {
      slots: {[slotIdx]: {$merge: {dataSourceId}}},
    });
    // Remove old data source if it is no longer referenced.
    return sanitizeAssembly(newAssembly);
  },

  updateSlotNames(assembly: LocalAssembly): LocalAssembly {
    let newAssembly = assembly;
    assembly.slots.forEach((slot, i) => {
      newAssembly = this.updateSlot(newAssembly, i, {
        name: getSlotName(assembly, i),
      });
    });
    return newAssembly;
  },

  addSlot(
    assembly: LocalAssembly,
    {activate = true, useLargerFragment = false}: {activate?: boolean; useLargerFragment?: boolean} = {}
  ): LocalAssembly {
    const dataSource = createDataSource();
    const slot = createSlot({
      dataSourceId: dataSource.id,
      useLargerFragment,
      slotName: getSlotName(assembly, assembly.slots.length),
    });
    let newAssembly = reactUpdate(assembly, {
      dataSources: {$push: [dataSource]},
      slots: {$push: [slot]},
    });
    newAssembly = this.updateSlotNames(newAssembly);
    if (activate) {
      newAssembly = Update.activateStep(newAssembly, newAssembly.slots.length - 1);
    }
    return newAssembly;
  },

  removeSlot(assembly: LocalAssembly, slotIdx: number): LocalAssembly {
    let newAssembly = reactUpdate(assembly, {
      slots: {$splice: [[slotIdx, 1]]},
    });
    newAssembly = this.updateSlotNames(newAssembly);
    assert(newAssembly.slots.length > 0, 'Only slot was removed from assembly');
    // Remove old data source if it is no longer referenced.
    return sanitizeAssembly(newAssembly);
  },

  nextSlot(assembly: LocalAssembly): LocalAssembly {
    const activeIdx = assembly.meta.activeSlotIndex;
    const nextSlotIdx = activeIdx === assembly.slots.length - 1 ? BULK_ASSEMBLY_FINALIZE_IDX : activeIdx + 1;
    return reactUpdate(assembly, {
      meta: {activeSlotIndex: {$set: nextSlotIdx}},
    });
  },

  previousSlot(assembly: LocalAssembly): LocalAssembly {
    assert(canGoToPreviousSlot(assembly), 'Cannot go to previous slot, see canGoToPreviousSlot');
    const activeIdx = assembly.meta.activeSlotIndex;
    const prevSlotIdx = activeIdx === BULK_ASSEMBLY_FINALIZE_IDX ? assembly.slots.length - 1 : activeIdx - 1;
    return reactUpdate(assembly, {
      meta: {activeSlotIndex: {$set: prevSlotIdx}},
    });
  },

  updateSlot(assembly: LocalAssembly, slotIdx: number, updates: Partial<SlotParams>): LocalAssembly {
    const newAssembly = reactUpdate(assembly, {
      slots: {[slotIdx]: {slotParams: {$merge: updates}}},
    });
    return sanitizeAssembly(newAssembly);
  },

  updateDataSource(
    assembly: LocalAssembly,
    dataSourceId: string,
    updates: Partial<LocalDataSource>
  ): LocalAssembly {
    return reactUpdate(assembly, {
      dataSources: applyUpdateForId(dataSourceId, {
        $merge: pick(updates, 'fileIds'),
        // TS gets confused by the reactUpdate types, so just use any for now.
        // oxlint-disable-next-line @typescript-eslint/no-explicit-any
      }) as any,
    });
  },

  activateStep(assembly: LocalAssembly, slotIdx: number): LocalAssembly {
    return reactUpdate(assembly, {
      meta: {activeSlotIndex: {$set: slotIdx}},
    });
  },

  updateForRun(assembly: LocalAssembly): LocalAssembly {
    return updateAssemblyState(assembly, BULK_ASSEMBLY_STATE.ASSEMBLING);
  },

  updateForCompletedRun(assembly: LocalAssembly): LocalAssembly {
    return updateAssemblyState(assembly, BULK_ASSEMBLY_STATE.ASSEMBLED);
  },

  updateForFailedRun(assembly: LocalAssembly): LocalAssembly {
    // TODO: Guarantee that only way to to ASSEMBLING is from VALIDATED,
    // and that no edits can be made to the during ASSEMBLING.
    return updateAssemblyState(assembly, BULK_ASSEMBLY_STATE.VALIDATED);
  },

  setTagSchemaId(assembly: LocalAssembly, tagSchemaId: string): LocalAssembly {
    return reactUpdate(assembly, {$merge: {tagSchemaId}});
  },

  setUseSequenceWorklist(assembly: LocalAssembly, useWorklist: boolean): LocalAssembly {
    if (useWorklist) {
      return reactUpdate(assembly, {
        sequenceSaveInformation: {$merge: {worklistParams: defaultWorklistParams}},
      });
    } else {
      return reactUpdate(assembly, {sequenceSaveInformation: {$merge: {worklistParams: null}}});
    }
  },

  setUsePrimerWorklist(assembly: LocalAssembly, useWorklist: boolean): LocalAssembly {
    if (useWorklist) {
      return reactUpdate(assembly, {
        primerSaveInformation: {$merge: {worklistParams: defaultWorklistParams}},
      });
    } else {
      return reactUpdate(assembly, {primerSaveInformation: {$merge: {worklistParams: null}}});
    }
  },

  setUseFragmentWorklist(assembly: LocalAssembly, useWorklist: boolean): LocalAssembly {
    if (useWorklist) {
      return reactUpdate(assembly, {
        fragmentSaveInformation: {$merge: {worklistParams: defaultWorklistParams}},
      });
    } else {
      return reactUpdate(assembly, {fragmentSaveInformation: {$merge: {worklistParams: null}}});
    }
  },

  setSequenceWorklistId(assembly: LocalAssembly, targetWorklistId: string): LocalAssembly {
    return reactUpdate(assembly, {sequenceSaveInformation: {worklistParams: {$merge: {targetWorklistId}}}});
  },

  setPrimerWorklistId(assembly: LocalAssembly, targetWorklistId: string): LocalAssembly {
    return reactUpdate(assembly, {primerSaveInformation: {worklistParams: {$merge: {targetWorklistId}}}});
  },

  setFragmentWorklistId(assembly: LocalAssembly, targetWorklistId: string): LocalAssembly {
    return reactUpdate(assembly, {fragmentSaveInformation: {worklistParams: {$merge: {targetWorklistId}}}});
  },

  setNewSequenceWorklistInfo(
    assembly: LocalAssembly,
    newWorklistInfo: BulkAssemblyNewWorklistInfo
  ): LocalAssembly {
    return reactUpdate(assembly, {sequenceSaveInformation: {worklistParams: {$merge: {newWorklistInfo}}}});
  },

  setNewPrimerWorklistInfo(
    assembly: LocalAssembly,
    newWorklistInfo: BulkAssemblyNewWorklistInfo
  ): LocalAssembly {
    return reactUpdate(assembly, {primerSaveInformation: {worklistParams: {$merge: {newWorklistInfo}}}});
  },

  setNewFragmentWorklistInfo(
    assembly: LocalAssembly,
    newWorklistInfo: BulkAssemblyNewWorklistInfo
  ): LocalAssembly {
    return reactUpdate(assembly, {fragmentSaveInformation: {worklistParams: {$merge: {newWorklistInfo}}}});
  },

  /**
   * Clear out both forms when switching between new worklist form and existing worklist form.
   */
  setSequenceWorklistFormType(assembly: LocalAssembly, worklistFormType: WorklistFormType): LocalAssembly {
    return reactUpdate(assembly, {
      sequenceSaveInformation: {
        worklistParams: {
          $merge: {
            worklistFormType,
            newWorklistInfo: null,
            targetWorklistId: null,
          },
        },
      },
    });
  },

  setPrimerWorklistFormType(assembly: LocalAssembly, worklistFormType: WorklistFormType): LocalAssembly {
    return reactUpdate(assembly, {
      primerSaveInformation: {
        worklistParams: {
          $merge: {
            worklistFormType,
            newWorklistInfo: null,
            targetWorklistId: null,
          },
        },
      },
    });
  },

  setFragmentWorklistFormType(assembly: LocalAssembly, worklistFormType: WorklistFormType): LocalAssembly {
    return reactUpdate(assembly, {
      fragmentSaveInformation: {
        worklistParams: {
          $merge: {
            worklistFormType,
            newWorklistInfo: null,
            targetWorklistId: null,
          },
        },
      },
    });
  },

  setSequenceFolderId(assembly: LocalAssembly, targetFolderId: string): LocalAssembly {
    return reactUpdate(assembly, {sequenceSaveInformation: {$merge: {targetFolderId}}});
  },

  setPrimerFolderId(assembly: LocalAssembly, targetFolderId: string): LocalAssembly {
    return reactUpdate(assembly, {primerSaveInformation: {$merge: {targetFolderId}}});
  },

  setFragmentFolderId(assembly: LocalAssembly, targetFolderId: string): LocalAssembly {
    return reactUpdate(assembly, {fragmentSaveInformation: {$merge: {targetFolderId}}});
  },

  updateForValidation(assembly: LocalAssembly): LocalAssembly {
    return updateAssemblyState(assembly, BULK_ASSEMBLY_STATE.VALIDATING);
  },

  updateForCompletedValidation(assembly: LocalAssembly, summary: Summary): LocalAssembly {
    return updateAssemblyState(assembly, BULK_ASSEMBLY_STATE.VALIDATED, {summary});
  },

  updateForFailedValidation(assembly: LocalAssembly): LocalAssembly {
    return updateAssemblyState(assembly, BULK_ASSEMBLY_STATE.UNVALIDATED, {summary: null});
  },
};

export function createAssembly(): LocalAssembly {
  let assembly: LocalAssembly = {
    id: _.uniqueId('bulkAssembly'),
    dataSources: [],
    slots: [],
    meta: {
      activeSlotIndex: 0,
      state: BULK_ASSEMBLY_STATE.UNASSEMBLED,
      summary: null,
    },
    topology: SequenceTopology.CIRCULAR,
    sequenceSaveInformation: deepCopy(defaultSaveInformation),
    primerSaveInformation: deepCopy(defaultSaveInformation),
    fragmentSaveInformation: deepCopy(defaultSaveInformation),
  };
  assembly = Update.addSlot(assembly, {activate: false, useLargerFragment: true});
  assembly = Update.addSlot(assembly, {activate: false});
  return assembly;
}

/** Exported for testing. */
export const defaultWorklistParams = {
  newWorklistInfo: null,
  targetWorklistId: null,
  worklistFormType: WorklistFormType.EXISTING,
};

const defaultSaveInformation = {
  tagSchemaId: null,
  worklistParams: null,
};

/**
 * Returns a new assembly, keeping the same slots and endpoints as the original, but resetting the meta state
 * and input and output files/worklists.
 */
export function cloneAssembly(sourceAssembly: LocalAssembly): LocalAssembly {
  return {
    id: _.uniqueId('bulkAssembly'),
    dataSources: sourceAssembly.dataSources.map((ds) => ({
      id: ds.id,
      fileIds: [],
    })),
    slots: sourceAssembly.slots.map((slot) => ({
      dataSourceId: slot.dataSourceId,
      dataSourceType: slot.dataSourceType,
      slotParams: {...slot.slotParams},
    })),
    meta: {
      activeSlotIndex: 0,
      state: BULK_ASSEMBLY_STATE.UNASSEMBLED,
      summary: null,
    },
    tagSchemaId: sourceAssembly.tagSchemaId,
    topology: sourceAssembly.topology,
    sequenceSaveInformation: deepCopy(defaultSaveInformation),
    primerSaveInformation: deepCopy(defaultSaveInformation),
    fragmentSaveInformation: deepCopy(defaultSaveInformation),
  };
}

// Access helpers

export function canRemoveSlot(assembly: LocalAssembly, slotIdx: number): boolean {
  return slotIdx !== 0;
}

export function canGoToPreviousSlot(assembly: LocalAssembly): boolean {
  return assembly.meta.activeSlotIndex !== 0;
}

export function getSlotIndicesForDataSource(
  assembly: LocalAssembly,
  dataSource: LocalDataSource | undefined,
  excludeIdx: number | null = null
): Array<number> {
  return assembly.slots
    .map((slot, i): [Slot, number] => [slot, i])
    .filter(([slot, i]) => (excludeIdx == null || i !== excludeIdx) && slot.dataSourceId === dataSource!.id)
    .map(([slot, i]) => i);
}

export function getDataSourceForSlot(assembly: LocalAssembly, slotIdx: number): LocalDataSource {
  const dataSource = _.findWhere(assembly.dataSources, {id: assembly.slots[slotIdx].dataSourceId});
  assert(Boolean(dataSource), 'Did not find data source for given slot');
  return dataSource!;
}

export function slotHasSequences(assembly: LocalAssembly, slotIdx: number | null): boolean {
  // eslint-disable-next-line benchlint/no-eqeqeq-null
  if (slotIdx === null) {
    return false;
  }

  const dataSource = getDataSourceForSlot(assembly, slotIdx);
  return dataSource.fileIds.length > 0;
}

export function getNextSlotIdx(assembly: LocalAssembly, slotIdx: number): number | null {
  if (slotIdx + 1 < assembly.slots.length) {
    return slotIdx + 1;
  }
  if (assembly.topology === SequenceTopology.CIRCULAR) {
    return 0;
  }

  return null;
}

export function getPreviousSlotIdx(assembly: LocalAssembly, slotIdx: number): number | null {
  if (slotIdx - 1 >= 0) {
    return slotIdx - 1;
  }
  if (assembly.topology === SequenceTopology.CIRCULAR) {
    return assembly.slots.length - 1;
  }

  return null;
}

export function getDefaultEndpointArgs(endpointType: EndpointType): Endpoint {
  switch (endpointType) {
    case EndpointType.ENZYME:
      return {type: endpointType, enzymeId: null};
    case EndpointType.HOMOLOGY:
      return {type: endpointType, bases: ''};
    case EndpointType.CONCATENATION:
    case EndpointType.GIBSON:
      return {type: endpointType, bases: null, position: 0};
    default:
      throw new Error(`Unexpected endpointType: ${endpointType}`);
  }
}

/**
 * Given a particular slot, returns a deterministic color to use for rendering.
 *
 * If multiple slots share the same data source (e.g. two slots sharing the backbone by using the
 * "use files from another step"), then the same color will be returned for both.
 */
export function getSlotFragmentColor(assembly: LocalAssembly, targetSlotIdx: number): string {
  const targetDataSourceId = assembly.slots[targetSlotIdx].dataSourceId;
  let index = targetSlotIdx;
  for (let i = 0; i < assembly.slots.length; i++) {
    if (assembly.slots[i].dataSourceId === targetDataSourceId) {
      index = i;
      break;
    }
  }
  return colors.shuffledPastelColors[index % colors.shuffledPastelColors.length];
}

export function getSlotName(assembly: LocalAssembly, targetSlotIdx: number): string {
  const hasBackbone = assembly.topology === SequenceTopology.CIRCULAR;
  const siblingIndexes = [];
  for (let i = 0; i < assembly.slots.length; i++) {
    if (i === 0 && hasBackbone) {
      if (i === targetSlotIdx) {
        siblingIndexes.push(i);
      }
    } else {
      siblingIndexes.push(i);
    }
  }
  const base = targetSlotIdx === 0 && hasBackbone ? 'Backbone' : 'Insert';
  return hasBackbone && targetSlotIdx === 0
    ? base
    : (hasBackbone && assembly.slots.length > 2) || (!hasBackbone && assembly.slots.length > 1)
      ? `${base} ${siblingIndexes.indexOf(targetSlotIdx) + 1}`
      : base;
}

export function getSlotNames(assembly: LocalAssembly, slotIndices: Array<number>): string {
  return listToReadable(slotIndices.map((idx) => getSlotName(assembly, idx)));
}

function getDataSourceErrors(assembly: LocalAssembly, id: string | undefined): Array<string> {
  const errors = [];
  const dataSource = _.findWhere(assembly.dataSources, {id});
  const dataSourceName = getSlotNames(assembly, getSlotIndicesForDataSource(assembly, dataSource));
  if (!dataSource || dataSource.fileIds.length === 0) {
    errors.push(`No sequences specified for ${dataSourceName}.`);
  }
  return errors;
}

export function getSlotSummary(assembly: LocalAssembly, idx: number): SlotSummary | null {
  if (!assembly.meta.summary) {
    return null;
  }
  return assembly.meta.summary.slots[idx];
}

function areBasesValid(bases: string | null): boolean {
  return Boolean(bases && biojs.util.isValidSequence(bases, biojs.util.NucleotideType.DNA));
}

export function isEndpointValid(endpoint: Endpoint): boolean {
  if (endpoint.type === EndpointType.ENZYME && endpoint.enzymeId == null) {
    return false;
  }
  if (endpoint.type === EndpointType.HOMOLOGY) {
    if (!areBasesValid(endpoint.bases)) {
      return false;
    }
  }
  if (endpoint.type === EndpointType.CONCATENATION) {
    const basesValid = areBasesValid(endpoint.bases);
    const positionValid = endpoint.position != null && endpoint.position >= 0;

    // Exactly one of bases or position must be valid
    if (basesValid === positionValid) {
      return false;
    }
  }
  return true;
}

function getSlotErrorsWithoutDataSourceErrors(assembly: LocalAssembly, slotIdx: number): Array<string> {
  const errors: Array<string> = [];
  const slot = assembly.slots[slotIdx];
  const slotName: string = getSlotName(assembly, slotIdx);
  if (!isEndpointValid(slot.slotParams.endpoints[0])) {
    errors.push(`Fragment end #1 is invalid for ${slotName}.`);
  }
  if (!isEndpointValid(slot.slotParams.endpoints[1])) {
    errors.push(`Fragment end #2 is invalid for ${slotName}.`);
  }
  const slotSummary = getSlotSummary(assembly, slotIdx);
  if (slotSummary) {
    const rowErrorCount = slotSummary.sequences.filter(({error}) => error).length;
    if (rowErrorCount === 1) {
      errors.push(`There is ${rowErrorCount} sequence error for ${slotName}.`);
    } else if (rowErrorCount > 1) {
      errors.push(`There are ${rowErrorCount} sequence errors for ${slotName}.`);
    }
    errors.push(...slotSummary.errors);
  }
  // If there are no known errors for this slot and there are no sequences, inform the user.
  const dataSource = _.findWhere(assembly.dataSources, {id: slot.dataSourceId});
  if (
    dataSource &&
    dataSource.fileIds.length &&
    errors.length === 0 &&
    slotSummary &&
    slotSummary.sequences.length === 0 &&
    assembly.meta.state !== BULK_ASSEMBLY_STATE.VALIDATING
  ) {
    errors.push(`No sequences found for ${slotName}.`);
  }
  return errors;
}

export function getSlotErrors(assembly: LocalAssembly, slotIdx: number): Array<string> {
  const errors: Array<string> = [];
  const slot = assembly.slots[slotIdx];
  errors.push(...getSlotErrorsWithoutDataSourceErrors(assembly, slotIdx));
  errors.push(...getDataSourceErrors(assembly, slot.dataSourceId));
  return errors;
}

export function getWorklistParamsErrors(
  worklistParams: WorklistInfo,
  outputType: BulkAssemblyOutputType
): Array<string> {
  const errors: Array<string> = [];
  const typeString = BULK_ASSEMBLY_OUTPUT_TYPE_TO_DISPLAY_STRING[outputType];
  if (worklistParams.worklistFormType === WorklistFormType.EXISTING) {
    if (worklistParams.newWorklistInfo != null) {
      errors.push(`Cannot add ${typeString} to both an existing worklist and a new worklist.`);
    }
    if (worklistParams.targetWorklistId == null) {
      errors.push(`A target worklist for ${typeString} is required.`);
    }
  } else {
    if (worklistParams.targetWorklistId != null) {
      errors.push(`Cannot add ${typeString} to both an existing worklist and a new worklist.`);
    }
    if (worklistParams.newWorklistInfo == null || !worklistParams.newWorklistInfo.name) {
      errors.push(`A worklist name for ${typeString} is required.`);
    }
  }
  return errors;
}

export function getErrors(assembly: LocalAssembly): Array<string> {
  const errors: Array<string> = assembly.meta.summary?.errors ? [...assembly.meta.summary.errors] : [];
  if (assembly.dataSources.length <= 1) {
    errors.push('At least 2 sets of sequences are needed to perform assembly.');
  }
  assembly.dataSources.forEach((dataSource) => {
    errors.push(...getDataSourceErrors(assembly, dataSource.id));
  });
  assembly.slots.forEach((slot, i) => {
    errors.push(...getSlotErrorsWithoutDataSourceErrors(assembly, i));
  });
  if (!assembly.sequenceSaveInformation.targetFolderId) {
    errors.push('A target folder is required.');
  }
  if (isGibsonAssembly(assembly) && !assembly.primerSaveInformation.targetFolderId) {
    errors.push('A target folder for primers is required.');
  }

  if (
    (assembly.fragmentSaveInformation.worklistParams?.targetWorklistId ||
      assembly.fragmentSaveInformation.worklistParams?.newWorklistInfo) &&
    !assembly.fragmentSaveInformation.targetFolderId
  ) {
    errors.push('When adding fragments to a worklist, a target folder for fragments is also required.');
  }

  if (assembly.sequenceSaveInformation.worklistParams != null) {
    errors.push(
      ...getWorklistParamsErrors(
        assembly.sequenceSaveInformation.worklistParams,
        BulkAssemblyOutputType.ASSEMBLED_SEQUENCES
      )
    );
  }

  if (assembly.primerSaveInformation.worklistParams != null) {
    errors.push(
      ...getWorklistParamsErrors(
        assembly.primerSaveInformation.worklistParams,
        BulkAssemblyOutputType.GIBSON_PRIMERS
      )
    );
  }

  if (assembly.fragmentSaveInformation.worklistParams != null) {
    errors.push(
      ...getWorklistParamsErrors(
        assembly.fragmentSaveInformation.worklistParams,
        BulkAssemblyOutputType.FRAGMENTS
      )
    );
  }

  return errors;
}

interface HomologyEndpointWithSlotName extends HomologyEndpoint {
  slotName: string;
}

interface EnzymeEndpointWithSlotName extends EnzymeEndpoint {
  slotName: string;
}

function getOtherEndpointsWithType(
  assembly: LocalAssembly,
  currentSlotIndex: number,
  endpointType: EndpointType
): Array<EnzymeEndpointWithSlotName | HomologyEndpointWithSlotName> {
  const endpoints: Array<EnzymeEndpointWithSlotName | HomologyEndpointWithSlotName> = [];
  assembly.slots.forEach((slot, i) => {
    for (const endpoint of slot.slotParams.endpoints) {
      if (i !== currentSlotIndex && endpoint.type === endpointType) {
        const augmented = {
          ...endpoint,
          slotName: getSlotName(assembly, i),
        } as EnzymeEndpointWithSlotName | HomologyEndpointWithSlotName;
        endpoints.push(augmented);
      }
    }
  });
  return endpoints;
}

export function getSuggestedEnzymes(
  assembly: LocalAssembly,
  slotIndex: number,
  endpointIndex: number
): Array<EnzymeEndpointWithSlotName & {enzymeId: number}> {
  const currentEndpoint = assembly.slots[slotIndex].slotParams.endpoints[endpointIndex];
  const enzymeEndpoints = getOtherEndpointsWithType(assembly, slotIndex, EndpointType.ENZYME).filter(
    (endpoint: EnzymeEndpointWithSlotName): endpoint is EnzymeEndpointWithSlotName & {enzymeId: number} =>
      Boolean(endpoint.enzymeId) && endpoint.enzymeId !== (currentEndpoint as EnzymeEndpoint).enzymeId
  );

  // eslint-disable-next-line no-restricted-syntax
  return _.uniq(enzymeEndpoints, (endpoint) => endpoint.enzymeId);
}

export function getSuggestedHomologyBases(
  assembly: LocalAssembly,
  slotIndex: number,
  endpointIndex: number
): Array<HomologyEndpointWithSlotName> {
  const currentEndpoint = assembly.slots[slotIndex].slotParams.endpoints[endpointIndex];
  const homologyEndpoints = getOtherEndpointsWithType(assembly, slotIndex, EndpointType.HOMOLOGY).filter(
    (endpoint: HomologyEndpointWithSlotName): endpoint is HomologyEndpointWithSlotName =>
      Boolean(endpoint.bases) && endpoint.bases !== (currentEndpoint as HomologyEndpoint).bases
  );

  // eslint-disable-next-line no-restricted-syntax
  return _.uniq(homologyEndpoints, (endpoint) => endpoint.bases);
}

export function isGibsonAssembly(assembly: LocalAssembly): boolean {
  return _.any(
    assembly.slots.flatMap((slot) => slot.slotParams.endpoints).map((endpoint) => endpoint.type),
    (endpointType) => endpointType === EndpointType.GIBSON
  );
}

export function slotsWithGibsonPrimerErrors(assembly: LocalAssembly): Array<number> {
  if (assembly.meta.summary == null) {
    return [];
  }
  return compact(
    assembly.slots.map((slot, i) =>
      _.any(slot.slotParams.endpoints, (endpoint) => endpoint.type === EndpointType.GIBSON) &&
      assembly.meta.summary!.slots[i]?.gibsonStatus === GibsonStatus.FAILURE
        ? i
        : null
    )
  );
}
